import {
  TimelineCategory,
  TimelineEventDTO,
  TimelineTone,
} from '@shared/apis/studentInsights.apis';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import { useMemo, useState } from 'react';

const PAGE_SIZE = 100;

export const CATEGORY_LABELS: Record<TimelineCategory, string> = {
  account: 'Account',
  enrollment: 'Enrollments',
  meeting: 'Meetings',
  feedback: 'Feedback',
  homework: 'Homework',
  report: 'Reports & invoices',
  payment: 'Payments',
  email: 'Emails',
  nps: 'NPS',
  university: 'Universities',
  flag: 'Flags',
  hubspot: 'HubSpot',
  admin: 'Admin changes',
};

const TONE_DOTS: Record<TimelineTone, string> = {
  positive: 'bg-emerald-400',
  negative: 'bg-red-400',
  warning: 'bg-amber-400',
  neutral: 'bg-customGrey',
};

type Props = {
  events: TimelineEventDTO[];
  initialHidden?: TimelineCategory[];
  groupBy?: 'month' | 'day';
  emptyMessage?: string;
};

const JourneyTimeline = ({
  events,
  initialHidden = [],
  groupBy = 'month',
  emptyMessage = 'Nothing recorded yet.',
}: Props) => {
  const [hidden, setHidden] = useState<TimelineCategory[]>(initialHidden);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const counts = useMemo(() => {
    const result = new Map<TimelineCategory, number>();
    events.forEach((event) => result.set(event.category, (result.get(event.category) ?? 0) + 1));
    return result;
  }, [events]);

  const filtered = useMemo(
    () => events.filter((event) => !hidden.includes(event.category)),
    [events, hidden]
  );

  const groups = useMemo(() => {
    const result: { label: string; items: TimelineEventDTO[] }[] = [];
    const format = groupBy === 'day' ? 'dddd, DD MMMM YYYY' : 'MMMM YYYY';

    filtered.slice(0, visibleCount).forEach((event) => {
      const label = moment(event.at).format(format);
      const last = result[result.length - 1];
      if (last?.label === label) last.items.push(event);
      else result.push({ label, items: [event] });
    });

    return result;
  }, [filtered, visibleCount, groupBy]);

  const toggle = (category: TimelineCategory) => {
    setHidden((previous) =>
      previous.includes(category)
        ? previous.filter((item) => item !== category)
        : [...previous, category]
    );
    setVisibleCount(PAGE_SIZE);
  };

  if (!events.length) {
    return <p className="text-sm text-customGrey">{emptyMessage}</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-2">
        {(Object.keys(CATEGORY_LABELS) as TimelineCategory[])
          .filter((category) => counts.get(category))
          .map((category) => (
            <button
              key={category}
              type="button"
              className={mergeClassNames(
                'rounded-full border px-3 py-1 text-xs transition-colors',
                hidden.includes(category)
                  ? 'border-white/10 text-customGrey'
                  : 'border-customPrimary bg-customPrimary/10 text-white'
              )}
              onClick={() => toggle(category)}
            >
              {CATEGORY_LABELS[category]} ({counts.get(category)})
            </button>
          ))}
      </div>

      {groups.map((group) => (
        <div key={group.label} className="flex flex-col gap-1">
          <p className="text-xs font-bold uppercase tracking-wide text-customGrey">{group.label}</p>
          <ul className="flex flex-col border-l border-white/10">
            {group.items.map((event) => (
              <li key={event.id} className="relative flex gap-3 py-2 pl-5">
                <span
                  className={mergeClassNames(
                    'absolute -left-[5px] top-[15px] h-2.5 w-2.5 rounded-full',
                    TONE_DOTS[event.tone]
                  )}
                />
                <span className="w-16 shrink-0 text-xs text-customGrey">
                  {moment(event.at).format(groupBy === 'day' ? 'HH:mm' : 'DD MMM')}
                </span>
                <div className="flex min-w-0 flex-col gap-0.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-white">{event.title}</span>
                    <span className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] uppercase tracking-wide text-customGrey">
                      {CATEGORY_LABELS[event.category]}
                    </span>
                    {event.url ? (
                      <a
                        href={event.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-customPrimary underline"
                      >
                        Open
                      </a>
                    ) : null}
                  </div>
                  {event.detail ? (
                    <span className="break-words text-xs text-customGrey">{event.detail}</span>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}

      {filtered.length > visibleCount ? (
        <button
          type="button"
          className="self-start text-xs text-customPrimary"
          onClick={() => setVisibleCount((value) => value + PAGE_SIZE)}
        >
          Show {Math.min(PAGE_SIZE, filtered.length - visibleCount)} more of{' '}
          {filtered.length - visibleCount} remaining
        </button>
      ) : null}
    </div>
  );
};

export default JourneyTimeline;
